import { useCallback } from 'react';
import { toast } from 'react-toastify';
import {
    useConnection,
    useWriteContract,
    usePublicClient,
} from 'wagmi';
import { parseUnits } from 'viem';
import {
    DEFAULT_ERROR_HEADING,
    GAS_FEES_ERROR,
} from '../../utils/ConstantMessages';
import { notifyError } from '../../components/Toast';
import BalanceOfAbi from '../../utils/BalanceOfAbi.json';
import {
    USDR_CONVERTER_ADDRESS,
    CONVERTER_USDT_ID,
    USDR_CONVERTER_ABI,
    IS_USDT_CONVERSION_ENABLED,
    CONVERTER_CAPACITY_MESSAGE,
    isConverterCapacityError,
} from '../../utils/usdrConverter';

// Deposit USDT from the EOA so it lands as USDR in the smart account: approve
// the converter if needed, then sellStable mints USDR 1:1 straight to
// `toAddress`. Same signature as useTransfer so handleDeposit can take either.
export const useConvertDeposit = () => {
    const { address: account, isConnected } = useConnection();
    const publicClient = usePublicClient();
    const writer = useWriteContract();

    const convertAndDeposit = useCallback(
        async (toAddress, amount, tokenConfig) => {
            try {
                if (!isConnected || !account) {
                    toast.error('Wallet not connected');
                    return false;
                }
                if (!IS_USDT_CONVERSION_ENABLED) {
                    toast.error('USDT deposits are not available right now');
                    return false;
                }

                const tokenAmount = parseUnits(amount.toString(), tokenConfig.tokenDecimals);

                const allowance = await publicClient.readContract({
                    address: tokenConfig.tokenAddress,
                    abi: BalanceOfAbi,
                    functionName: 'allowance',
                    args: [account, USDR_CONVERTER_ADDRESS],
                });
                if (allowance < tokenAmount) {
                    const approveHash = await writer.mutateAsync({
                        address: tokenConfig.tokenAddress,
                        abi: BalanceOfAbi,
                        functionName: 'approve',
                        args: [USDR_CONVERTER_ADDRESS, tokenAmount],
                    });
                    await publicClient.waitForTransactionReceipt({ hash: approveHash });
                }

                const gasPrice = await publicClient.getGasPrice();
                const adjustedGasPrice = gasPrice * 2n;

                const gasEstimate = await publicClient.estimateContractGas({
                    address: USDR_CONVERTER_ADDRESS,
                    abi: USDR_CONVERTER_ABI,
                    functionName: 'sellStable',
                    args: [CONVERTER_USDT_ID, toAddress, tokenAmount],
                    account,
                });

                const totalGasCost = gasEstimate * adjustedGasPrice;
                const nativeBalance = await publicClient.getBalance({ address: account });
                if (nativeBalance < totalGasCost) {
                    notifyError(DEFAULT_ERROR_HEADING, GAS_FEES_ERROR);
                    return false;
                }

                const hash = await writer.mutateAsync({
                    address: USDR_CONVERTER_ADDRESS,
                    abi: USDR_CONVERTER_ABI,
                    functionName: 'sellStable',
                    args: [CONVERTER_USDT_ID, toAddress, tokenAmount],
                    gas: gasEstimate,
                    gasPrice: adjustedGasPrice,
                });
                await publicClient.waitForTransactionReceipt({ hash });

                return { transactionHash: hash, hash };
            } catch (error) {
                console.error('Convert deposit error:', error);
                if (isConverterCapacityError(error)) {
                    notifyError(DEFAULT_ERROR_HEADING, CONVERTER_CAPACITY_MESSAGE);
                    return false;
                }
                toast.error(
                    error?.shortMessage ||
                    error?.message ||
                    'Transaction failed'
                );
                throw error;
            }
        },
        [account, isConnected, publicClient, writer]
    );

    return convertAndDeposit;
};
